import React from 'react';

import { addDays, getDate, getYear, parse, setDate, setYear } from 'date-fns';
import { find } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import Panel from '@/components/ui/box/Panel';
import Paper from '@/components/ui/box/Paper';
import Button from '@/components/ui/buttons/Button';
import DatePicker from '@/components/ui/forms/DatePicker';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import Select from '@/components/ui/forms/Select';
import PageTitle from '@/components/ui/headers/PageTitle';
import useModal from '@/hooks/common/useModal';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import useCurrentUser from '@/hooks/store/useCurrentUser';

import AbsencesConfirmPage from './AbsencesConfirmPage';
import styles from './AbsencesCreatePage.module.scss';
import { contactTypeOptions } from './constants';
import AbsencesFormInputsOnOrAfterReason from './form/AbsencesFormInputsOnOrAfterReason';
import AbsencesFormStaticInput from './form/AbsencesFormStaticInput';
import {
  convertToSubmitValuesWithOmId,
  validationSchema,
} from './form/constants';
import AbsencesFooter from './parts/AbsencesFooter';
import { type FormValues, type SubmitValues, type Value } from './types';

interface TimeLimitState {
  timeLimit?: string | null;
}

const AbsencesCreatePage: React.FC = () => {
  const [contactType, setContactType] = React.useState<Value>(null);
  const [isConfirming, setIsConfirming] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<SubmitValues | null>(null);
  const { organizationMembershipId = '' } = useParams();
  const [currentUser] = useCurrentUser();
  const { showModal } = useModal();
  const location = useLocation();
  const state = location.state as TimeLimitState;

  const {
    handleSubmit,
    registerWithInnerRef,
    formState: { validFields, dirtyFields, errorMessages, isValid },
    clearErrors,
  } = useReactHookForm<FormValues>({
    validationSchema: validationSchema(),
    defaultValues: {
      'absence.date': '',
      contact_type: '',
      reason: '',
      go_to_school_at: '',
      leave_school_at: '',
      disease_name: '',
      symptom: '',
      body_temperature: '',
      doctors_instruction: '',
      relationship: '',
      transportation: '',
      detail_comment: '',
    },
  });

  const organizationMembership = find(currentUser?.organization_memberships, [
    'id',
    organizationMembershipId,
  ]);

  const setTimeLimit = () => {
    if (state?.timeLimit) {
      return state.timeLimit;
    }

    if (
      organizationMembership &&
      organizationMembership?.organization_management &&
      organizationMembership?.organization_management?.absence_time_limit
    ) {
      return organizationMembership.organization_management.absence_time_limit;
    }

    return '';
  };

  const studentTimeLimit = setTimeLimit();

  const now = new Date();

  const timeLimit = studentTimeLimit
    ? parse(studentTimeLimit, 'HH:mm', now)
    : now;

  const minDate = now <= timeLimit ? now : addDays(now, 1);
  const nextYear = setYear(now, getYear(now) + 1);
  const maxDate = setDate(nextYear, getDate(nextYear) - 1);

  const student = (currentUser?.organization_memberships || []).find(
    ({ id }) => id === organizationMembershipId,
  );

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      const submitValues = convertToSubmitValuesWithOmId(
        values,
        organizationMembershipId,
      );
      setFormValues(submitValues);
      setIsConfirming(true);
    },
    [organizationMembershipId],
  );

  const onCancel = React.useCallback(() => {
    showModal('AbsencesCancelFormModal', {
      url: `/absences/member/${organizationMembershipId || ''}`,
    });
  }, [showModal, organizationMembershipId]);

  const onBackInConfirm = React.useCallback(() => {
    setIsConfirming(false);
  }, []);

  if (!student) return <ErrorPage type="not_found" />;
  if (isConfirming && formValues)
    return (
      <AbsencesConfirmPage
        organizationMembershipId={organizationMembershipId}
        values={formValues}
        onBack={onBackInConfirm}
        onCancel={onCancel}
        submitStatus="created"
      />
    );

  return (
    <SingleColumnTemplate>
      <PageTitle
        title="欠席連絡の入力"
        rightLinkLabel="キャンセル"
        rightLinkUrl="#"
        onRightLinkClick={(ev) => {
          ev.preventDefault();
          onCancel();
        }}
        rightLinkColor="gray"
      />

      <div className={styles.contents}>
        {/* SEE: https://github.com/react-hook-form/react-hook-form/discussions/8020 */}
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Jumbotron>
            <Paper>
              <Panel title="欠席連絡詳細">
                <AbsencesFormStaticInput label="生徒名" value={student.name} />
                <FormGroup>
                  <LabelWithStatus required valid={validFields['absence.date']}>
                    日付
                  </LabelWithStatus>
                  <Field>
                    <DatePicker
                      minDate={minDate}
                      maxDate={maxDate}
                      inputProps={registerWithInnerRef('absence.date')}
                    />
                    <ErrorList errors={errorMessages['absence.date']} />
                  </Field>
                </FormGroup>
                <FormGroup>
                  <LabelWithStatus required valid={validFields.contact_type}>
                    内容
                  </LabelWithStatus>
                  <Field>
                    <Select
                      data-testid="contact_type"
                      inputProps={registerWithInnerRef('contact_type')}
                      placeholder="選択してください"
                      options={contactTypeOptions}
                      onChange={(val) => setContactType(val)}
                    />
                    <ErrorList errors={errorMessages.contact_type} />
                  </Field>
                </FormGroup>
                <AbsencesFormInputsOnOrAfterReason
                  registerWithInnerRef={registerWithInnerRef}
                  validFields={validFields}
                  dirtyFields={dirtyFields}
                  contactType={contactType}
                  errorMessages={errorMessages}
                  clearErrors={clearErrors}
                />
              </Panel>
            </Paper>
          </Jumbotron>

          <AbsencesFooter>
            <div className={styles.buttons}>
              <Button
                color="primary"
                disabled={!isValid}
                type="submit"
                size="l"
                className={styles.button}
              >
                <FormattedMessage id="forms.button.next" />
              </Button>

              <Button
                color="link"
                size="l"
                onClick={onCancel}
                className={styles.button}
              >
                <FormattedMessage id="forms.button.cancel" />
              </Button>
            </div>
          </AbsencesFooter>
        </form>
      </div>
    </SingleColumnTemplate>
  );
};

export default AbsencesCreatePage;
